import { getBrandSignupAutoAcceptedCountriesOptions } from "@faire/retailer-visitor-shared/features/MakerApplication/MakerApplicationCountries";
import { isE2E } from "@faire/retailer-visitor-shared/lib/isE2E";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { getSettingMarketingSMSEligibleCountries } from "@faire/retailer-visitor-shared/settings/getSettingMarketingSMSEligibleCountries";
import { useMakerSignUpFormContextStore } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/MakerSignUpFormStore";
import { useMakerSignUpContextStore } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/MakerSignUpStore";
import { MakerApplicationStages } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/useMakerSignUpForm";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { IOption } from "@faire/web--source/slate/Dropdown";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import referralInviteClicked from "@faire/web-api--source/endpoints/www/api/brand-lead/referral-invite-clicked/post";
import { useQuery as useBrandQuery } from "@faire/web-api--source/endpoints/www/api/brand/brandToken/get-hooks";
import { QueryParameters as GetInvitingRetailersParameters } from "@faire/web-api--source/endpoints/www/api/retailer-to-maker/inviting-retailers/get";
import { useQuery as useGetInvitingRetailersQuery } from "@faire/web-api--source/endpoints/www/api/retailer-to-maker/inviting-retailers/get-hooks";
import { useQuery as useGetPublicRetailerQuery } from "@faire/web-api--source/endpoints/www/api/retailer/public/retailerToken/get-hooks";
import { trackBrandLeadsBrandsFormMobileNavigationFirstBackClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormMobileNavigationFirstBack";
import { trackBrandLeadsInboundBrandReferralLinkView } from "@faire/web-api--source/events/brandLeads/view/inboundBrandReferralLink";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { IBrandLeadReferralInviteClickedRequest } from "@faire/web-api--source/indigofair/data/IBrandLeadReferralInviteClickedRequest";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { route as brandAdApplicationRoute } from "@faire/web-api--source/routes/www/apply/ad";
import { route as brandToBrandApplyRoute } from "@faire/web-api--source/routes/www/apply/brandTokenOrAlias";
import { route as retailerToBrandApplyRoute } from "@faire/web-api--source/routes/www/apply/r/retailerTokenSuffix";
import { route as brandsHomeRoute } from "@faire/web-api--source/routes/www/brands";
import once from "lodash/once";
import { useCallback, useEffect, useMemo } from "react";
import { matchPath, useHistory } from "react-router-dom";

export const trackInviteClickedOnce = once(
  (
    isM2M: boolean,
    retailerTokenFromPath: string | null,
    email: string,
    brandTokenOrAlias?: string
  ) => {
    if (isM2M) {
      if (!brandTokenOrAlias) {
        return;
      }
      referralInviteClicked(
        IBrandLeadReferralInviteClickedRequest.build({
          brand_token_alias: brandTokenOrAlias,
          brand_email_address: email,
        })
      );
      return;
    }

    if (!retailerTokenFromPath) {
      return;
    }

    referralInviteClicked(
      IBrandLeadReferralInviteClickedRequest.build({
        retailer_token: retailerTokenFromPath,
        brand_email_address: email,
      })
    );
  }
);

interface ReferrerInformation {
  name: string | undefined;
  image: IImage | undefined;
}

export const useMakerSignUp = () => {
  const [{ applicationStage }, setMakerState] = useMakerSignUpContextStore([
    "applicationStage",
  ]);
  const [{ country, email, brandLeadToken, _source }] =
    useMakerSignUpFormContextStore([
      "country",
      "email",
      "brandLeadToken",
      "_source",
    ]);

  const history = useHistory();
  const { strictLocalize } = useStrictLocalization();
  const pathname = getLocationOrThrow().pathname;

  const tokenFromPath = pathname.split("/").pop();
  const retailerTokenFromPath = tokenFromPath
    ? "r_".concat(tokenFromPath.toLowerCase())
    : null;
  const brandTokenOrAlias = tokenFromPath ?? undefined;

  const isAd = Boolean(
    matchPath(removeLocaleFromUrl(pathname), {
      path: removeLocaleFromUrl(brandAdApplicationRoute),
      exact: true,
    })
  );

  const isR2M = Boolean(
    matchPath(removeLocaleFromUrl(pathname), {
      path: removeLocaleFromUrl(retailerToBrandApplyRoute),
      exact: true,
    })
  );

  const isM2M =
    !isAd &&
    Boolean(
      matchPath(removeLocaleFromUrl(pathname), {
        path: removeLocaleFromUrl(brandToBrandApplyRoute),
        exact: true,
      })
    );

  const isReferral = isR2M || isM2M;

  // Initialize applicationStage only once
  useEffect(() => {
    const initialStage =
      isReferral || isAd
        ? MakerApplicationStages.LANDING
        : MakerApplicationStages.FORM;

    // Only set if it hasn't been initialized yet
    if (applicationStage === undefined) {
      setMakerState({ applicationStage: initialStage });
    }
  }, [isReferral, isAd, applicationStage, setMakerState]);

  const setApplicationStage = useCallback(
    (stage: MakerApplicationStages) => {
      setMakerState({ applicationStage: stage });
    },
    [setMakerState]
  );

  const { data: referringRetailerData, isLoading: isReferringRetailerLoading } =
    useGetPublicRetailerQuery(retailerTokenFromPath ?? "", {
      enabled: Boolean(retailerTokenFromPath) && isReferral,
    });

  const { data: referringBrandData, isLoading: isReferringBrandLoading } =
    useBrandQuery(tokenFromPath ?? "", undefined, {
      enabled: Boolean(tokenFromPath) && isM2M,
    });

  const {
    data: referringRetailersData,
    isLoading: isReferringRetailersLoading,
  } = useGetInvitingRetailersQuery(
    GetInvitingRetailersParameters.build({
      brandLeadToken: brandLeadToken ?? "",
    }),
    {
      enabled: Boolean(brandLeadToken),
    }
  );

  const isLoading =
    isReferringRetailerLoading ||
    isReferringBrandLoading ||
    isReferringRetailersLoading;

  useEffect(() => {
    if (isReferral && email) {
      trackInviteClickedOnce(
        isM2M,
        retailerTokenFromPath,
        email,
        brandTokenOrAlias
      );
    }
  }, [isReferral, email, isM2M, retailerTokenFromPath, brandTokenOrAlias]);

  useEffect(() => {
    if (referringRetailerData?.retailer?.token || referringBrandData?.token) {
      trackBrandLeadsInboundBrandReferralLinkView(
        referringRetailerData?.retailer?.token ?? "",
        referringBrandData?.token ?? ""
      );
    }
  }, [referringRetailerData, referringBrandData]);

  const getSource = (): IBrandLead.Source => {
    if (_source) {
      return _source;
    }

    if (isAd) {
      return IBrandLead.Source.DIGITAL_ADS;
    }

    if (isR2M) {
      return IBrandLead.Source.RETAILER_REFERRAL;
    }

    if (isM2M) {
      return IBrandLead.Source.BRAND_REFERRAL;
    }

    return IBrandLead.Source.ORGANIC;
  };

  const source: IBrandLead.Source = getSource();

  const getReferrerInformation = (): ReferrerInformation | null => {
    if (isR2M && referringRetailerData?.retailer) {
      return {
        name: referringRetailerData.retailer.name,
        image: referringRetailerData.retailer.profile_image,
      };
    }

    if (isM2M && referringBrandData) {
      return {
        name: referringBrandData.name,
        image: referringBrandData.squared_image,
      };
    }

    return null;
  };

  const referrerInformation: ReferrerInformation | null =
    getReferrerInformation();

  const otherReferringRetailerCount: number = useMemo(() => {
    if (!isR2M || !retailerTokenFromPath) {
      return 0;
    }

    return (
      referringRetailersData?.retailers.filter(
        (retailer) => retailer.retailer_token !== retailerTokenFromPath
      ).length ?? 0
    );
  }, [isR2M, referringRetailersData, retailerTokenFromPath]);

  const toggleAcceptedTerms = useCallback(() => {
    setMakerState((prev) => ({
      acceptedTerms: !prev.acceptedTerms,
    }));
  }, [setMakerState]);

  const shouldShowSMSOptIn = useMemo(() => {
    const sessionCountry = getSessionCountry();
    const isE2EEnabled = isE2E();
    const eligibleCountries = getSettingMarketingSMSEligibleCountries();

    return (
      isE2EEnabled ||
      eligibleCountries.some(
        (eligibleCountry) => eligibleCountry === sessionCountry
      )
    );
  }, []);

  const onBackClick = useCallback(() => {
    trackBrandLeadsBrandsFormMobileNavigationFirstBackClick();
    history.push(brandsHomeRoute);
  }, [history]);

  const countryDropdownOption = useMemo(() => {
    const options = getBrandSignupAutoAcceptedCountriesOptions(strictLocalize);
    const retailerIso = country?.isoCode;
    if (retailerIso && retailerIso !== "Other") {
      let optionArr: IOption[] = [];
      options.forEach((option) => {
        if (option.value === retailerIso) {
          optionArr = [option, ...optionArr];
        } else {
          optionArr.push(option);
        }
      });
      return optionArr;
    }
    return options;
  }, [country, strictLocalize]);

  return {
    displaySource: _source,
    source,
    referrerInformation,
    otherReferringRetailerCount,
    toggleAcceptedTerms,
    shouldShowSMSOptIn,
    onBackClick,
    isLoading,
    isAd,
    isM2M,
    isR2M,
    isReferral,
    applicationStage,
    setApplicationStage,
    countryDropdownOption,
    referringBrand: referringBrandData,
    referringRetailer: referringRetailerData?.retailer,
    setMakerState,
    retailerTokenFromPath,
    brandTokenOrAlias,
  };
};
